import { axiosConfig } from "../../services/axiosConfig";

interface FeedResponse {
  id: string;
  author: string;
  author_first_name: string;
  title: string;
  organization_id: string;
  department_id: string;
  visibility: number;
  visibility_label: string;
  content: string;
  attachments: string;
  like_count: number;
  dislike_count: number;
  comments_count: number;
  created_on: string;
  comments: any[];
  user_has_liked?: boolean;
}

interface CreateFeedPayload {
  title: string;
  content: string;
  organization_id: string;
  department_id?: string | null;
  position_id?: string | null;
  visibility: number;
  attachments?: string | null;
}

interface FeedReactionPayload {
  feed: string;
  reaction_type: number;
}

interface CreateFeedCommentPayload {
  feed: string;
  comment: string;
}

class FeedService {
  getFeeds = async (params?: {
    my_city?: boolean;
  }): Promise<FeedResponse[]> => {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (params?.my_city === true) {
        queryParams.append("my_city", "true");
      }

      const url = queryParams.toString()
        ? `/city_collab/feeds/?${queryParams.toString()}`
        : "/city_collab/feeds/";

      const response = await axiosConfig.get(url);
      console.log("FeedService - Raw response:", response);

      // Handle different response structures
      const responseData = response?.data;

      // If response itself is an array
      if (Array.isArray(response)) {
        console.log(
          "FeedService - Response is array, length:",
          response.length
        );
        return response;
      }

      // If response.data is an array (most common)
      if (Array.isArray(responseData)) {
        console.log(
          "FeedService - Response.data is array, length:",
          responseData.length
        );
        return responseData;
      }

      // If response.data.results is an array (pagination)
      if (responseData?.results && Array.isArray(responseData.results)) {
        console.log(
          "FeedService - Response.data.results is array, length:",
          responseData.results.length
        );
        return responseData.results;
      }

      // If response.data.data is an array (nested)
      if (responseData?.data && Array.isArray(responseData.data)) {
        console.log(
          "FeedService - Response.data.data is array, length:",
          responseData.data.length
        );
        return responseData.data;
      }

      console.warn("FeedService - Unexpected response structure:", {
        response,
        responseData,
        type: typeof responseData,
        isArray: Array.isArray(responseData),
      });

      return [];
    } catch (error: any) {
      console.error("FeedService - Error in getFeeds:", error);
      console.error("FeedService - Error details:", {
        message: error?.message,
        response: error?.response,
        data: error?.response?.data,
      });
      throw error;
    }
  };

  createFeed = async (
    payload: CreateFeedPayload,
    notifyMessage: boolean = true
  ): Promise<FeedResponse> => {
    try {
      if (!payload) {
        throw new Error("Payload is required");
      }

      if (!payload.title || !payload.content) {
        throw new Error("Title and content are required");
      }

      if (!payload.organization_id) {
        throw new Error("Organization ID is required");
      }

      console.log("FeedService - Creating feed with payload:", payload);

      // Prepare the request payload
      const requestPayload: any = {
        title: payload.title,
        content: payload.content,
        organization_id: payload.organization_id,
        visibility: payload.visibility || 1,
      };

      // Add optional fields if provided
      if (payload.department_id) {
        requestPayload.department_id = payload.department_id;
      }

      if (payload.position_id) {
        requestPayload.position_id = payload.position_id;
      }

      // Add attachments if provided
      if (payload.attachments) {
        requestPayload.attachments = payload.attachments;
      }

      const response = await axiosConfig.post(
        "/city_collab/feeds/",
        requestPayload,
        {
          notifyMessage,
        }
      );

      console.log("FeedService - Create feed response:", response);
      return response.data;
    } catch (error: any) {
      console.error("FeedService - Error creating feed:", error);
      throw error;
    }
  };

  addFeedReaction = async (
    payload: FeedReactionPayload,
    notifyMessage: boolean = false
  ): Promise<any> => {
    try {
      if (!payload?.feed || !payload.reaction_type) {
        throw new Error("Feed ID and reaction type are required");
      }

      console.log("FeedService - Adding feed reaction with payload:", payload);

      const response = await axiosConfig.post(
        "/city_collab/feed-reactions/",
        payload,
        {
          notifyMessage,
        }
      );

      console.log("FeedService - Add reaction response:", response);
      return response.data;
    } catch (error: any) {
      console.error("FeedService - Error adding feed reaction:", error);
      throw error;
    }
  };

  createFeedComment = async (
    payload: CreateFeedCommentPayload,
    notifyMessage: boolean = true
  ): Promise<any> => {
    try {
      if (!payload?.feed || !payload.comment) {
        throw new Error("Feed ID and comment are required");
      }

      console.log("FeedService - Creating feed comment with payload:", payload);

      const response = await axiosConfig.post(
        "/city_collab/feed-comments/",
        payload,
        {
          notifyMessage,
        }
      );

      console.log("FeedService - Create comment response:", response);
      return response.data;
    } catch (error: any) {
      console.error("FeedService - Error creating feed comment:", error);
      throw error;
    }
  };
}

const instance = new FeedService();

export default instance;
